import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProjectManufacturerProductListing } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { SelectedManufacturerListing } from '../../api/graphQL/types/product.type'

/**
 * Combines existing manufacturers from product selected items with newly selected manufacturers
 * and returns a sorted list of all selected manufacturers.
 * 
 * @param productSelected - Array of selected product listings that may contain manufacturers
 * @param selectedManufacturers - Array of newly selected manufacturers
 * @returns Sorted array of all selected manufacturers combined
 */
export const combineAndSortManufacturers = (
    productSelected: Partial<ProjectManufacturerProductListing>[] = [],
    selectedManufacturers: SelectedManufacturerListing[] = []
): SelectedManufacturerListing[] => {
    const manufacturerTypeUppercase = ListingLevel.Manufacturer.split(' ').join('').toUpperCase()
    
    // Filter and map existing manufacturers from productSelected
    const existingManufacturers = productSelected
        ?.filter((item: Partial<ProjectManufacturerProductListing>) => {
            const itemTypeUppercase = item.type?.split(' ').join('').toUpperCase()
            return itemTypeUppercase === manufacturerTypeUppercase
        })
        ?.map((item: Partial<ProjectManufacturerProductListing>, index: number) => ({
            listingId: item.listingId || '',
            firmName: item.firmName || '',
            type: manufacturerTypeUppercase as ListingLevel
        } as SelectedManufacturerListing)) || []
    
    // Combine existing and newly selected manufacturers
    const allSelectedManufacturers = [
        ...existingManufacturers,
        ...selectedManufacturers.map(manufacturer => ({
            ...manufacturer,
            type: manufacturer.type.split(' ').join('').toUpperCase() as ListingLevel
        })),
    ]
    
    // Sort by firm name alphabetically
    return allSelectedManufacturers.sort((a, b) => a.firmName.localeCompare(b.firmName))
}

/**
 * Toggles a manufacturer in the selected manufacturers list.
 * If the manufacturer is already selected, it removes it.
 * If the manufacturer is not selected and not already in the product selected list, it adds it.
 * 
 * @param listingId - The listing ID of the manufacturer
 * @param listingLevel - The listing level (should be 'Manufacturer')
 * @param firmName - The firm name of the manufacturer
 * @param selectedManufacturers - Current array of selected manufacturers
 * @param productSelectedListingIds - Array of listing IDs already added to the project
 * @returns Updated array of selected manufacturers
 */
export const toggleManufacturerSelection = (
    listingId: string,
    listingLevel: ListingLevel,
    firmName: string,
    selectedManufacturers: SelectedManufacturerListing[] = [],
    productSelectedListingIds: string[] = []
): SelectedManufacturerListing[] => {
    // Only process manufacturer level items
    if (listingLevel !== ListingLevel.Manufacturer) {
        return selectedManufacturers
    }
    
    // Don't add manufacturers that are already added to the project
    if (productSelectedListingIds.includes(listingId)) {
        return selectedManufacturers
    }
    
    const safeSelectedManufacturers = selectedManufacturers
    const found = safeSelectedManufacturers.find(
        (item: SelectedManufacturerListing) => item.listingId === listingId
    )
    
    if (found) {
        // Remove listingId if already present
        return safeSelectedManufacturers.filter(
            (item: SelectedManufacturerListing) => item.listingId !== listingId
        )
    } else {
        // Add listingId if not present, then sort by firmName
        const newList = [
            ...safeSelectedManufacturers,
            {
                listingId,
                firmName,
                type: listingLevel.split(' ').join('').toUpperCase(),
            } as SelectedManufacturerListing,
        ].sort((a, b) => a.firmName.localeCompare(b.firmName))
        return newList
    }
}

/**
 * Filters product selected items to only include those matching the specified element ID.
 * Used to filter manufacturers for a specific product type.
 * 
 * @param productSelected - Array of selected products/manufacturers
 * @param elementId - The element ID to filter by
 * @returns Filtered array of product selected items matching the element ID
 */
export const filterProductsByElementId = <T extends { elementId?: string | null }>(
    productSelected: T[] = [],
    elementId: string | null | undefined
): T[] => {
    if (!elementId) {
        return []
    }
    
    return productSelected.filter(product => product.elementId === elementId)
}

/**
 * Determines the new selection state when toggling between "Select All" and "Unselect All" for manufacturers.
 * If manufacturers are currently selected, returns an empty array (unselect all).
 * If no manufacturers are selected, returns all available manufacturers that are not already added to the project.
 * 
 * @param selectedManufacturers - Current array of selected manufacturers
 * @param searchListings - Array of search result listings to filter from
 * @param productSelectedListingIds - Array of listing IDs already added to the project
 * @returns Updated array of selected manufacturers (empty if unselecting all, populated if selecting all)
 */
export const selectUnselectAllManufacturers = (
    selectedManufacturers: SelectedManufacturerListing[] = [],
    searchListings: Array<{ listingId?: string | null; firmName?: string | null; type?: ListingLevel | null }> = [],
    productSelectedListingIds: string[] = []
): SelectedManufacturerListing[] => {
    // If there are selected manufacturers, unselect all
    if (selectedManufacturers && selectedManufacturers.length > 0) {
        return []
    }
    
    // Otherwise, select all manufacturers that are not already added to the project
    const availableManufacturers = searchListings.filter(
        (listing) =>
            listing.type === ListingLevel.Manufacturer &&
            !productSelectedListingIds.includes(listing.listingId as string)
    )
    
    if (availableManufacturers && availableManufacturers.length > 0) {
        const newSelectedManufacturers = availableManufacturers.map(
            (listing) => ({
                listingId: listing.listingId as string,
                firmName: listing.firmName as string,
                type: ListingLevel.Manufacturer.split(' ').join('').toUpperCase(),
            } as SelectedManufacturerListing)
        ).sort((a, b) => a.firmName.localeCompare(b.firmName))
        
        return newSelectedManufacturers
    }
    
    return []
}